import {
  Box,
  Button,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Cards from "../../ReusableComponents/Cards/Cards";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import EditIcon from "@mui/icons-material/Edit";
import { PersonOff as PersonOffIcon } from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AddPharmacy from "./AddPharmacy";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import SearchBar from "../../ReusableComponents/Search/SearchBar";
import CustomCard from "../../ReusableComponents/CustomCard/CustomCard";
import { ButtonStyles } from "../../Styles/ButtonStyles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import en_ja from "../../translations/en_ja";
import PersonIcon from '@mui/icons-material/Person';

const Pharmacy = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [tab, setTab] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pharmacyData, setPharmacyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pharmacyToDeactivate, setPharmacyToDeactivate] = useState(null);

  const [cardsData, setCardsData] = useState({});

  const getAllCards = () => {
    api
      .get("/v1/admin/pharmacies/dashboard-data")
      .then((res) => {
        setCardsData(res?.data ?? {});
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    getAllPharmacies();
  }, []);

  const getAllPharmacies = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/v1/admin/all-pharmacies`);
      const finalData = res?.data?.data || [];
      setPharmacyData(finalData);
      await getAllCards();
    } catch (err) {
      console.error(err);
      setError(true);
      setSnackbarMessage(en_ja["Failed to fetch pharmacies"]);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPharmacy = () => {
    setEditMode(false);
    setSelectedPharmacy(null);
    setDialogOpen(true);
  };

  const handleEditPharmacy = (pharmacy) => {
    setEditMode(true);
    setSelectedPharmacy(pharmacy);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedPharmacy(null);
    setEditMode(false);
  };

  const handleDialogSubmit = () => {
    getAllPharmacies();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarType("success");
  };

  const handleDeactivate = (pharmacy) => {
    setPharmacyToDeactivate(pharmacy);
    setConfirmDialogOpen(true);
  };

  const handleCancelDeactivate = () => {
    setConfirmDialogOpen(false);
    setPharmacyToDeactivate(null);
  };

  const handleConfirmDeactivate = async () => {
    if (!pharmacyToDeactivate) return;

    const pharmacyId = pharmacyToDeactivate.id;
    const currentStatus = pharmacyToDeactivate.status;
    const obj = { status: !currentStatus };

    setConfirmDialogOpen(false);
    setPharmacyToDeactivate(null);

    try {
      const res = await api.put(
        `/v1/admin/pharmacy/status/${pharmacyId}`,
        obj
      );

      const updatedStatus = res?.data?.pharmacy?.status
        ? en_ja["Active"]
        : en_ja["Inactive"];

      setSnackbarMessage(
        `${en_ja["Pharmacy status updated to"]} ${updatedStatus}`
      );
      setSnackbarType("success");
      setSnackbarOpen(true);

      getAllPharmacies();
    } catch (err) {
      console.error(err);
      setSnackbarMessage(en_ja["Could not update Status"]);
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  // Calculate filtered pharmacies count
  const filteredPharmacies = (pharmacyData || []).filter(
    (p) =>
      p.pharmacy_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.phone_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ mt: 2 }}>
      <CustomSnackbar
        isOpen={snackbarOpen}
        type={snackbarType}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

      {/* Cards Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 3,
          width: "100%",
        }}
      >
        <Cards
          title={en_ja["Total Pharmacies"]}
          value={cardsData?.total ?? 0}
          icon={<LocalPharmacyIcon sx={{ color: "white", fontSize: 24 }} />}
        />
        <Cards
          title={en_ja["Active Pharmacies"]}
          value={cardsData?.totalActive ?? 0}
          icon={<CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />}
        />
        <Cards
          title={en_ja["Inactive Pharmacies"]}
          value={cardsData?.totalInactive ?? 0}
          icon={<CancelIcon sx={{ color: "white", fontSize: 24 }} />}
        />
        <Cards
          title={en_ja["Avg Revenue/Pharmacies"]}
          value={cardsData?.avgRevenue ?? 0}
          icon={<MonetizationOnIcon sx={{ color: "white", fontSize: 24 }} />}
        />
      </Box>

      {/* Add Button */}
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", my: 3 }}
      >
        <Button sx={ButtonStyles} onClick={handleAddPharmacy}>
          {en_ja["Add Pharmacy"]}
        </Button>
      </Box>

      {/* Tab with count - Fixed version */}
      <CustomCard
        boxSx={{ flexDirection: "row", justifyContent: "space-between", flexWrap: "wrap" }}
        innerBoxSx={{
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        <SearchBar value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

        <Tabs value={tab}>
          <Tab
            label={
              <Box sx={{ fontWeight: 700 }}>
                {en_ja["All"] || "All"}
                {" "}
                {!loading && <Box component="span" sx={{ color: "red" }}>
                  ({filteredPharmacies.length})
                </Box>}
              </Box>
            }
          />
        </Tabs>
      </CustomCard>

      {/* Pharmacy List */}
      <Typography
        variant="h5"
        fontWeight="700"
        color="#1a1a1a"
        sx={{ fontSize: "20px", mb: 3 }}
      >
        {en_ja["Pharmacies"]}
      </Typography>

      {loading ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            {en_ja["Loading pharmacies..."]}
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            {en_ja["Failed to load pharmacies"]}
          </Typography>
        </Box>
      ) : pharmacyData?.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            {en_ja["No pharmacy data found"]}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
            mt: 3,
          }}
        >
          {filteredPharmacies.map((pharmacy, index) => (
            <Paper
              key={pharmacy.id || pharmacy._id || `pharmacy-${index}`}
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid #e9ecef",
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {pharmacy.pharmacy_name}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={en_ja["Edit Pharmacy"] || "Edit Pharmacy"} arrow>
                    <IconButton size="small" onClick={() => handleEditPharmacy(pharmacy)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      pharmacy?.status
                        ? en_ja["Deactivate Pharmacy"] || "Deactivate Pharmacy"
                        : en_ja["Activate Pharmacy"] || "Activate Pharmacy"
                    }
                    arrow
                  >
                    <IconButton size="small" onClick={() => handleDeactivate(pharmacy)}>
                      {pharmacy?.status ? (
                        <PersonIcon sx={{ color: "#dc3545" }} />
                      ) : (
                        <PersonOffIcon sx={{ color: "rgb(0 0 0 / 54%)" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box>
                <Chip
                  label={
                    pharmacy.status
                      ? en_ja["ACTIVE"] || "ACTIVE"
                      : en_ja["INACTIVE"] || "INACTIVE"
                  }
                  size="small"
                  sx={{
                    backgroundColor: pharmacy?.status ? "#22c55e" : "#ef4444",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "10px",
                    mb: 1,
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" sx={{ color: "#6c757d" }} />
                <Typography variant="body2" color="text.secondary">
                  {pharmacy.address}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" sx={{ color: "#6c757d" }} />
                <Typography variant="body2" color="text.secondary">
                  {pharmacy.phone_number}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon fontSize="small" sx={{ color: "#6c757d" }} />
                <Typography variant="body2" color="text.secondary">
                  {pharmacy.email}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary">
                {en_ja["Registered"]}: {new Date(pharmacy.created_at).toISOString().split("T")[0]}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDeactivate}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1 }}
          >
            {pharmacyToDeactivate?.status
              ? (en_ja["Deactivate Pharmacy"] ?? "Deactivate Pharmacy")
              : (en_ja["Activate Pharmacy"] ?? "Activate Pharmacy")}
          </Typography>

          <IconButton onClick={handleCancelDeactivate}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", pt: 1, pb: 3 }}>
          {pharmacyToDeactivate && (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {en_ja["Are you sure you want to"]}{" "}
                {pharmacyToDeactivate.status
                  ? (en_ja["deactivate"] || "deactivate")
                  : (en_ja["activate"] || "activate")}{" "}
                "{pharmacyToDeactivate.pharmacy_name}"?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {en_ja["This action can be reversed later by"]}{" "}
                {pharmacyToDeactivate.status
                  ? (en_ja["reactivating"] || "reactivating")
                  : (en_ja["deactivating"] || "deactivating")}{" "}
                {en_ja["the account"] || "the account"}.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleCancelDeactivate}
            sx={{ textTransform: "none" }}
          >
            {en_ja["No, Keep"]} {pharmacyToDeactivate?.status ? en_ja["Active"] : en_ja["Inactive"]}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDeactivate}
            color={pharmacyToDeactivate?.status ? "error" : "success"}
            sx={{ textTransform: "none" }}
          >
            {en_ja["Yes,"]} {pharmacyToDeactivate?.status ? en_ja["Deactivate"] : en_ja["Activate"]}
          </Button>
        </DialogActions>
      </Dialog>

      <AddPharmacy
        dialogOpen={dialogOpen}
        onClose={handleDialogClose}
        editMode={editMode}
        pharmacyData={selectedPharmacy}
        onSubmit={handleDialogSubmit}
        setSnackbarType={setSnackbarType}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default Pharmacy;